import { ArtistImage } from "models/ArtistImage";
import { useEffect, useState, useCallback, useRef } from "react";
import { ApiService } from "services/ApiService";
import ArtistImageComponent from "./ArtistImageComponent";

interface ArtistImagesProps {
    artistId: string;
    isVisitor: boolean;
    refreshKey: number;
    onClick?: (image: ArtistImage) => void;
}

const ArtistImages: React.FC<ArtistImagesProps> = ({ artistId, isVisitor, refreshKey, onClick }) => {
    const [artistImages, setArtistImages] = useState<ArtistImage[]>([]);
    const [currentIndex, setCurrentIndex] = useState<number>(0);
    const [dragIndex, setDragIndex] = useState<number | null>(null);
    const imagesRef = useRef<ArtistImage[]>([]);

    // Keep ref in sync so drag-end always sees the latest array.
    useEffect(() => { imagesRef.current = artistImages; }, [artistImages]);

    useEffect(() => {
        const fetchImages = async () => {
            const response = await ApiService.getArtistImages(artistId);
            if (response.success && response.data) {
                const sorted = [...response.data].sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0));
                setArtistImages(sorted);
            }
        };
        fetchImages();
    }, [artistId, refreshKey]);

    const handleNext = () => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % artistImages.length);
    };

    const handlePrev = () => {
        setCurrentIndex((prevIndex) =>
            (prevIndex - 1 + artistImages.length) % artistImages.length
        );
    };

    const handleImageClick = (image: ArtistImage) => {
        if (onClick) onClick(image);
    };

    const handleImageUpdate = (updatedImage: ArtistImage) => {
        setArtistImages((prevImages) =>
            prevImages.map((img) =>
                img.id === updatedImage.id ? updatedImage : img
            )
        );
    };

    const handleDeleteClick = async (image: ArtistImage): Promise<void> => {
        try {
            const response = await ApiService.deleteArtistImage(artistId, image.id);
            if (response.success) {
                setArtistImages((prevImages) => {
                    const updatedImages = prevImages.filter((img) => img.id !== image.id);
                    if (currentIndex >= updatedImages.length) {
                        setCurrentIndex(Math.max(0, updatedImages.length - 1));
                    }
                    return updatedImages;
                });
            }
        } catch (error) {
            console.error("Error deleting artist image:", error);
        }
    };

    // --- Drag-and-drop reorder ---
    const handleDragStart = useCallback((index: number) => {
        setDragIndex(index);
    }, []);

    const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
        e.preventDefault();
        if (dragIndex === null || dragIndex === index) return;
        setArtistImages((prev) => {
            const updated = [...prev];
            const [moved] = updated.splice(dragIndex, 1);
            updated.splice(index, 0, moved);
            return updated;
        });
        setDragIndex(index);
    }, [dragIndex]);

    const handleDragEnd = useCallback(async () => {
        setDragIndex(null);
        // Use ref to always get the latest array, avoiding stale closure.
        const current = imagesRef.current;
        const items = current.map((img, i) => ({ imageId: img.id, rank: i + 1 }));
        try {
            await ApiService.reorderArtistImages(artistId, items);
        } catch (error) {
            console.error("Error persisting reorder:", error);
        }
    }, [artistId]);

    const thumbnailImages = artistImages.slice(0, Math.min(artistImages.length, 8));

    return (
        <div className="relative w-full h-screen bg-gray-100">
            {artistImages.length > 0 && (
                <div className="absolute inset-0 flex items-center justify-center">
                    <ArtistImageComponent
                        artistImage={artistImages[currentIndex]}
                        artistId={artistId}
                        isVisitor={isVisitor}
                        onClick={() => handleImageClick(artistImages[currentIndex])}
                        onDeleteClick={() => handleDeleteClick(artistImages[currentIndex])}
                        onImageUpdate={handleImageUpdate}
                        isEditable={!isVisitor}
                    />
                </div>
            )}

            {/* Navigation Buttons */}
            {artistImages.length > 1 && (
                <>
                    <button
                        onClick={handlePrev}
                        className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-gray-800 text-white p-3 rounded-full shadow-lg hover:bg-gray-700 focus:outline-none"
                    >
                        &#8592;
                    </button>
                    <button
                        onClick={handleNext}
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-gray-800 text-white p-3 rounded-full shadow-lg hover:bg-gray-700 focus:outline-none"
                    >
                        &#8594;
                    </button>
                </>
            )}

            {/* Drag-and-drop thumbnail strip */}
            {!isVisitor && artistImages.length > 1 && (
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2 bg-black/50 p-2 rounded-lg">
                    {thumbnailImages.map((img, i) => (
                        <div
                            key={img.id}
                            draggable
                            onDragStart={() => handleDragStart(i)}
                            onDragOver={(e) => handleDragOver(e, i)}
                            onDragEnd={handleDragEnd}
                            onClick={() => setCurrentIndex(i)}
                            className={`w-16 h-16 flex-shrink-0 rounded overflow-hidden border-2 cursor-grab active:cursor-grabbing ${
                                i === currentIndex ? 'border-blue-400' : 'border-transparent'
                            } ${dragIndex === i ? 'opacity-50' : ''}`}
                        >
                            <img
                                src={`${process.env.REACT_APP_API_BASE_URL}/artists/${artistId}/ArtistImages/${img.id}`}
                                alt={img.name}
                                className="w-full h-full object-cover"
                            />
                        </div>
                    ))}
                    {artistImages.length > 8 && (
                        <span className="text-white text-sm self-center ml-2">
                            +{artistImages.length - 8} more
                        </span>
                    )}
                </div>
            )}
        </div>
    );
};

export default ArtistImages;